import { useContext, useEffect, useRef, useState } from "react";
import { CesiumComponentRef, Viewer } from "resium";
import {
  Cartesian3,
  Cartographic,
  Viewer as CesiumViewer,
  Color,
  defined,
  Entity,
  HorizontalOrigin,
  IonGeocodeProviderType,
  Terrain,
  VerticalOrigin,
  viewerDragDropMixin,
  Math,
  createGooglePhotorealistic3DTileset,
  Ion,
  GoogleMaps,
  KmlDataSource,
  PropertyBag,
  GeoJsonDataSource,
  Math as CesiumMath,
} from "cesium";
import { MapSettings } from "../../../common/components/resiumMap/mapSettings";
import { environment } from "../../../common/env";
import { Location } from "./models";
import { Utils } from "../../../common/helpers/utils";
import shp from "shpjs";
import dxfParser from "dxf-parser";
import { IShapeFile } from "../../earthtwin/models";
import { Themes } from "../../../common/enums/themes";
import Context from "../../../common/components/utils/context";
import { IPOI } from "../../matterportViewer/matterportViewer3";
import { IAsset } from "../../admin/models";

interface IPostEditMap {
  onClick?: (
    longitude: number,
    latitude: number,
    altitude: number,
    seaLevel: number
  ) => void;
  location?: Location;
  shapeFile?: IShapeFile;
  poiFile?: IPOI;
  asset?: IAsset;
}

export const PostEditMap = (props: IPostEditMap) => {
  const context = useContext(Context);

  Ion.defaultAccessToken = environment.Cesium_Default_Access_Token;
  GoogleMaps.defaultApiKey = environment.Google_Api_Key;

  const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);
  const pinEntityRef = useRef<Entity | null>(null);

  const [enable3DMap, setEnable3DMap] = useState<boolean>(false);
  const [isShowLables, setIsShowLables] = useState<boolean>(true);

  const getCurrentPosition = async () => {
    if (ref.current && ref.current.cesiumElement) {
      ref.current.cesiumElement.camera.flyTo({
        destination: Cartesian3.fromDegrees(
          context.currentPosition.longitude,
          context.currentPosition.latitude,
          context.currentPosition.altitude || 12000
        ),
        duration: 4,
      });
    }
  };

  useEffect(() => {
    initMap();
    if (
      props.location &&
      props.location.longitude &&
      props.location.latitude &&
      props.location.altitude
    ) {
      loadExistingPost();
    } else if (props.asset) {
      goToAsset();
    } else if (props.poiFile) {
      loadPOIFile(props.poiFile);
    } else if (props.shapeFile) {
      loadShapeFile(props.shapeFile);
    } else {
      getCurrentPosition();
    }
  }, [
    props.location?.longitude,
    props.location?.latitude,
    props.location?.altitude,
    ref.current && ref.current.cesiumElement,
    props.poiFile,
    props.shapeFile,
    props.asset,
  ]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      viewer.baseLayerPicker.viewModel.selectedImagery = isShowLables
        ? viewer.baseLayerPicker.viewModel.imageryProviderViewModels[1]
        : viewer.baseLayerPicker.viewModel.imageryProviderViewModels[0];
    }
  }, [ref.current?.cesiumElement, isShowLables]);

  const initMap = async () => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      viewer.scene.setTerrain(
        Terrain.fromWorldTerrain({
          requestWaterMask: true,
          requestVertexNormals: true,
        })
      );

      viewer.camera.changed.addEventListener(function () {
        const cartographic = viewer.camera.positionCartographic;
        context.setCurrentPosition(
          CesiumMath.toDegrees(cartographic.latitude),
          CesiumMath.toDegrees(cartographic.longitude),
          cartographic.height
        );
      });
    }
  };

  const goToAsset = async () => {
    if (props.location && ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      viewer.camera.flyTo({
        destination: Cartesian3.fromDegrees(0, 0, 12000),
        duration: 4,
      });
    }
  };

  const loadExistingPost = async () => {
    if (props.location && ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      const { longitude, latitude, altitude } = props.location;

      if (longitude && latitude && altitude) {
        viewer.camera.flyTo({
          destination: Cartesian3.fromDegrees(longitude, latitude, 12000),
          duration: 4,
        });

        if (pinEntityRef.current) {
          viewer.entities.remove(pinEntityRef.current);
        }

        const position = Cartesian3.fromDegrees(longitude, latitude, altitude);

        pinEntityRef.current = viewer.entities.add({
          position: position,
          billboard: {
            image: "/img/pin_red.png",
            verticalOrigin: VerticalOrigin.BOTTOM,
            horizontalOrigin: HorizontalOrigin.CENTER,
            color: Color.WHITE.withAlpha(1.0),
          },
        });
      }
    }
  };

  const handleClick = async (e: any) => {
    const viewer = ref.current?.cesiumElement;
    if (!viewer) return;

    const cartesian = viewer.scene.pickPosition(e.position);

    if (defined(cartesian)) {
      var cartographic = Cartographic.fromCartesian(cartesian);

      const latitude = parseFloat(
        Math.toDegrees(cartographic.latitude).toFixed(6)
      );
      const longitude = parseFloat(
        Math.toDegrees(cartographic.longitude).toFixed(6)
      );

      let height = cartographic.height;
      const seaLevel = await Utils.getElevation(latitude, longitude);

      if (pinEntityRef.current) {
        viewer.entities.remove(pinEntityRef.current);
      }

      const position = Cartesian3.fromDegrees(longitude, latitude, height);

      // Add a new pin (entity) at the clicked position
      pinEntityRef.current = viewer.entities.add({
        position: position,
        billboard: {
          image: "/img/pin_red.png",
          verticalOrigin: VerticalOrigin.BOTTOM,
          horizontalOrigin: HorizontalOrigin.CENTER,
          color: Color.WHITE.withAlpha(1.0),
        },
      });

      if (props.onClick) {
        props.onClick(longitude, latitude, height, seaLevel);
      }
    }
  };

  const handleShowLablesToggle = () => setIsShowLables(!isShowLables);

  const handleEnable3DMapToggle = async () => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      if (enable3DMap) {
        if (viewer.scene.primitives.length > 0) {
          for (let i = 0; i < viewer.scene.primitives.length; i++) {
            const primitive = viewer.scene.primitives.get(i);
            if (primitive && primitive.isCesium3DTileset) {
              viewer.scene.primitives.remove(primitive);
            }
          }
        }

        setEnable3DMap(false);
      } else {
        const tileset = await createGooglePhotorealistic3DTileset({
          key: environment.Google_Api_Key,
        });
        viewer.scene.primitives.add(tileset);

        setEnable3DMap(true);
      }
    }
  };

  const loadPOIFile = (poiFile: IPOI) => {
    console.log("Selected POI file:", poiFile);
    if (!ref.current || !ref.current.cesiumElement) return;
    const viewer = ref.current.cesiumElement;

    const entity = viewer.entities.getById(poiFile.id);
    if (entity) {
      viewer.entities.remove(entity);
    }

    const position = Cartesian3.fromDegrees(
      poiFile.longitude,
      poiFile.latitude,
      poiFile.altitude
    );

    const orientation = Utils.calculatePOIOrientation(poiFile);

    viewer.entities.add({
      id: poiFile.id,
      name: poiFile.name,
      position: position,
      model: {
        uri: poiFile.modelUrl,
        minimumPixelSize: 10,
        maximumScale: 256,
      },
      orientation: orientation,
    });

    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(
        poiFile.longitude,
        poiFile.latitude,
        poiFile.altitude + 100
      ),
    });
  };

  const loadShapeFile = async (shapeFile: IShapeFile) => {
    console.log("Selected file:", shapeFile);

    var view2DObjectResult = await view2DObjectByUrl(shapeFile);
    if (view2DObjectResult && view2DObjectResult.errorMessage) {
      context.setAlert({
        message: `Failed to display object!
                 ${view2DObjectResult.errorMessage}`,
        theme: Themes.Danger,
      });
    }
  };

  const view2DObjectByUrl = async (shapeFile: IShapeFile) => {
    var blob = await Utils.url2blob(shapeFile.url);
    if (blob) {
      var fileName = shapeFile.url.split("/").pop() || "";
      var files = [new File([blob], fileName)];
      const file = files[0];

      return await view2DObject(file, shapeFile.id);
    }
  };

  const view2DObject = async (file: File, id: string) => {
    if (!ref.current || !ref.current.cesiumElement) return;

    const viewer = ref.current.cesiumElement;

    var errorMessage;

    const fileExtension = file.name.split(".").pop()?.toLocaleLowerCase();
    if (fileExtension === "geojson") {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        const geojson = JSON.parse(e.target.result);

        const geojsonDataSource = await GeoJsonDataSource.load(geojson, {
          clampToGround: true,
        });
        await viewer.dataSources.add(geojsonDataSource);

        var zoomedToGeoJSON = viewer.flyTo(geojsonDataSource);
        if (!zoomedToGeoJSON) {
          errorMessage = "Unable to load GeoJSON file!";
        }
      };

      reader.readAsText(file);
    } else if (fileExtension === "zip") {
      try {
        const data = await file.arrayBuffer();
        const geojson = await shp(data);

        const shpDataSource = await GeoJsonDataSource.load(geojson, {
          clampToGround: true,
        });
        await viewer.dataSources.add(shpDataSource);

        var entities = shpDataSource.entities.values;
        for (let entity of entities) {
          entity.name = file.name;
          entity.properties = new PropertyBag({ itemId: id });
        }

        var zoomedToSHP = viewer.zoomTo(shpDataSource);
        if (!zoomedToSHP) {
          errorMessage = "Unable to load SHP file!";
        }
      } catch (error) {
        errorMessage = "Unable to load SHP file!";
      }
    } else if (fileExtension === "dxf") {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          const parser = new dxfParser();
          const dxfData = parser.parseSync(e.target.result);
          const geoJSONData = Utils.convertDXFToGeoJSON(dxfData);
          const dxfDataSource = await GeoJsonDataSource.load(geoJSONData, {
            clampToGround: true,
          });
          await viewer.dataSources.add(dxfDataSource);

          var entities = dxfDataSource.entities.values;
          for (let entity of entities) {
            entity.name = file.name;
            entity.properties = new PropertyBag({ itemId: id });
          }

          var zoomedToDXF = viewer.zoomTo(dxfDataSource);
          if (!zoomedToDXF) {
            errorMessage = "Unable to load DXF file!";
          }
        } catch (error) {
          errorMessage = "Unable to load DXF file!";
        }
      };
      reader.readAsText(file);
    } else if (fileExtension === "kml" || fileExtension === "kmz") {
      try {
        const kmlDataSource = await KmlDataSource.load(file, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas,
          clampToGround: true,
        });
        await viewer.dataSources.add(kmlDataSource);
        var zoomedToKML = await viewer.zoomTo(kmlDataSource);
        if (!zoomedToKML) {
          errorMessage = "Unable to load KML file!";
        }
      } catch (error) {
        errorMessage = "Unable to load KML file!";
      }
    } else {
      errorMessage = "No valid .zip, .dxf, .kml or .kmz file!";
    }

    var newFileObj = {
      inputFile: file,
      errorMessage: errorMessage,
    };
    return newFileObj;
  };

  return (
    <>
      <div
        className="fixed top-20 left-10 w-[300px] bg-transparent flex flex-col gap-2"
        style={{ zIndex: 10 }}
      >
        <MapSettings
          onShowLablesToggle={handleShowLablesToggle}
          onEnable3DMapToggle={handleEnable3DMapToggle}
          isEnable3DMap={enable3DMap}
          isShowLables={isShowLables}
        />
      </div>
      <Viewer
        full
        ref={ref}
        animation={false}
        timeline={false}
        homeButton={true}
        sceneModePicker={false}
        baseLayerPicker={true}
        navigationHelpButton={false}
        shouldAnimate={true}
        requestRenderMode={true}
        extend={viewerDragDropMixin}
        onClick={(e) => handleClick(e)}
        geocoder={IonGeocodeProviderType.GOOGLE}
      ></Viewer>
    </>
  );
};
